import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import PageTransition from '../components/PageTransition';
import Toast, { ToastType } from '../components/Toast';
import DatePicker from '../components/DatePicker';
import { getWarranties, searchWarranties } from '../services/warrantyService';
import SerialSearchModal from '../components/SerialSearchModal';
import { IslemAdi, getStatusById, getIdByStatus } from '../constants/status';
import { ActionType } from '../types/ServiceRequest';

import { PAGE_SIZES } from '../constants/pagination';
import { useUser } from '../contexts/UserContext';
import { useMenu } from '../contexts/MenuContext';
import { useRipple } from '../hooks/useRipple';

export default function Warranties() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const { userData } = useUser();
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  
  // Scroll yönünü takip etmek için state ve ref
  const [showNewRequestButton, setShowNewRequestButton] = useState(true);
  const [showSearchButton, setShowSearchButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);
  const scrollSaveTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // URL parametrelerinden state'leri oku
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page) : 1;
  });
  
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const statuses = searchParams.get('statuses');
    return statuses ? statuses.split(',').map(id => getStatusById(parseInt(id))).filter(s => s) : [];
  });
  
  const [startDate, setStartDate] = useState<string>(() => {
    return searchParams.get('startDate') || '';
  });
  
  const [endDate, setEndDate] = useState<string>(() => {
    return searchParams.get('endDate') || '';
  });
  
  const [isSearchMode, setIsSearchMode] = useState(() => {
    return searchParams.get('searchMode') === 'true';
  });
  
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('searchQuery') || '';
  });
  
  const [searchType, setSearchType] = useState<'serial' | 'invoice'>(() => {
    const type = searchParams.get('searchType');
    return (type === 'serial' || type === 'invoice') ? type : 'serial';
  });
  
  const [showFilters, setShowFilters] = useState(() => {
    return searchParams.get('showFilters') === 'true';
  });
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isActiveSearch, setIsActiveSearch] = useState(false);
  
  // Arama öncesi filtre seçimlerini kaydetmek için
  const [preSearchStatuses, setPreSearchStatuses] = useState<string[]>([]);
  const [preSearchStartDate, setPreSearchStartDate] = useState<string>('');
  const [preSearchEndDate, setPreSearchEndDate] = useState<string>('');
  
  // Ripple hook
  const { ripples, createRipple } = useRipple<number>();
  
  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' as ToastType });

  // Durum seçenekleri
  const statusOptions = [
    IslemAdi.ONAY_BEKLIYOR,
    IslemAdi.ONAYLANDI, 
    IslemAdi.IPTAL_EDILDI
  ];

  // URL parametrelerini güncelleme fonksiyonu
  const updateUrlParams = (params: {
    page?: number;
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    searchMode?: boolean;
    searchQuery?: string;
    searchType?: 'serial' | 'invoice';
    showFilters?: boolean;
  }) => {
    const currentParams = Object.fromEntries(searchParams);
    
    // Page parametresi
    if (params.page !== undefined) {
      if (params.page === 1) {
        delete currentParams.page;
      } else {
        currentParams.page = params.page.toString();
      }
    }
    
    // Statuses parametresi
    if (params.statuses !== undefined) {
      if (params.statuses.length === 0) {
        delete currentParams.statuses;
      } else {
        const statusIds = params.statuses.map(status => getIdByStatus(status)).filter(id => id > 0);
        currentParams.statuses = statusIds.join(',');
      }
    }
    
    // Date parametreleri
    if (params.startDate !== undefined) {
      if (params.startDate === '') {
        delete currentParams.startDate;
      } else {
        currentParams.startDate = params.startDate;
      }
    }
    
    if (params.endDate !== undefined) {
      if (params.endDate === '') {
        delete currentParams.endDate;
      } else {
        currentParams.endDate = params.endDate;
      }
    }
    
    // Search parametreleri
    if (params.searchMode !== undefined) {
      if (params.searchMode === false) {
        delete currentParams.searchMode;
        delete currentParams.searchQuery;
        delete currentParams.searchType;
      } else {
        currentParams.searchMode = 'true';
      }
    }
    
    if (params.searchQuery !== undefined && params.searchMode !== false) {
      if (params.searchQuery === '') {
        delete currentParams.searchQuery;
      } else {
        currentParams.searchQuery = params.searchQuery;
      }
    }
    
    if (params.searchType !== undefined && params.searchMode !== false) {
      currentParams.searchType = params.searchType;
    }
    
    // ShowFilters parametresi
    if (params.showFilters !== undefined) {
      if (params.showFilters === false) {
        delete currentParams.showFilters;
      } else {
        currentParams.showFilters = 'true';
      }
    }
    
    // Manuel URL string oluştur
    const queryParts: string[] = [];
    Object.entries(currentParams).forEach(([key, value]) => {
      if (value) {
        queryParts.push(`${key}=${value}`);
      }
    });
    
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    
    // Navigate ile URL'yi güncelle
    navigate(`${window.location.pathname}${queryString}`, { replace: true });
  };

  // Toast functions
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  // Tarih formatı
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // .NET tarih formatından JavaScript Date'e çevir
    const dateMatch = dateString.match(/\/Date\((\d+)\)\//);
    if (dateMatch) {
      const timestamp = parseInt(dateMatch[1]);
      const date = new Date(timestamp);
      return date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    // Normal tarih formatı
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  // Garanti bitiş tarihi formatı - özel koşullar ile
  const formatWarrantyEndDate = (warranty: any) => {
    // GarantiDurumu 1 ise ve GarantiBitisTarihi null ise "Başlamamış"
    if (warranty.GarantiDurumu === 1 && (!warranty.GarantiBitisTarihi || warranty.GarantiBitisTarihi === null)) {
      return 'Başlamamış';
    }
    
    // Normal tarih formatı
    return formatDate(warranty.GarantiBitisTarihi);
  };

  // Durum rengi
  const getStatusColor = (status: string) => {
    switch (status) {
      case IslemAdi.ONAY_BEKLIYOR:
        return 'bg-gradient-to-br from-blue-100 via-sky-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300';
      case IslemAdi.ONAYLANDI:
        return 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300';
      case IslemAdi.IPTAL_EDILDI:
        return 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300';
      default:
        return 'bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 text-gray-800 dark:from-gray-900/30 dark:via-gray-900/25 dark:to-gray-800/30 dark:text-gray-300';
    }
  };

  // Garanti durumu rengi - WarrantyStart.tsx'teki mantığa göre
  const getWarrantyStatusColor = (warranty: any) => {
    // İptal durumunda kırmızı
    if (warranty.SonIslem === ActionType.CANCELLED) {
      return 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300';
    }
    
    // Garanti durumu 1 ise (garantili) yeşil
    if (warranty.GarantiDurumu === 1) {
      return 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300';
    }
    
    // Garanti dışı durumunda kırmızı
    return 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300';
  };

  // Garanti detaylarına git
  const handleWarrantyClick = (event: React.MouseEvent, warrantyId: number) => {
    createRipple(event, warrantyId, () => {
      navigate(`/warranty/${warrantyId}`);
    });
  };

  // Durum filtresi değişikliği
  const handleStatusChange = (status: string) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    
    setSelectedStatuses(newStatuses);
    setCurrentPage(1);
    
    // URL güncelle
    updateUrlParams({
      statuses: newStatuses,
      page: 1
    });
  };

  // Tarih formatını API formatına çevir
  const formatDateForAPI = (dateString: string, addDays: number = 0) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    date.setDate(date.getDate() + addDays);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}, ${month}, ${day}`;
  };

  // React Query ile normal garantileri çek (arama modunda değilken)
  const {
    data: warrantiesData,
    isLoading: loading,
    isRefetching: refreshing,
    error: warrantiesError
  } = useQuery({
    queryKey: ['warranties', currentPage, selectedStatuses, startDate, endDate, userData?.ShowAllCoupon, userData?.User],
    queryFn: async () => {
      const statusFilter = selectedStatuses.length > 0 ? selectedStatuses : undefined;
      
      // Tarih filtresi oluştur
      const dateFilter = (startDate || endDate) ? {
        startDate: startDate ? formatDateForAPI(startDate, 0) : undefined,
        endDate: endDate ? formatDateForAPI(endDate, 1) : undefined
      } : undefined;
      
      const response = await getWarranties(currentPage, PAGE_SIZES.SERVICE_REQUESTS, statusFilter, dateFilter, userData?.ShowAllCoupon, userData?.User);
      return response;
    },
    enabled: isComponentMounted && !isSearchMode,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // React Query ile arama sonuçlarını çek (arama modundayken)
  const {
    data: searchResultsData,
    isLoading: searchLoading,
    isRefetching: searchRefreshing,
    error: searchError
  } = useQuery({
    queryKey: ['warrantiesSearch', searchQuery, searchType, currentPage, userData?.ShowAllCoupon, userData?.User],
    queryFn: async () => {
      const response = await searchWarranties(searchQuery, searchType, currentPage, PAGE_SIZES.SERVICE_REQUESTS, userData?.ShowAllCoupon, userData?.User);
      return response;
    },
    enabled: isComponentMounted && isSearchMode && !!searchQuery,
    staleTime: 1 * 60 * 1000,
    gcTime: 3 * 60 * 1000,
  });

  // Aktif veri setini belirle
  const activeData = isSearchMode ? searchResultsData : warrantiesData;
  const warranties = activeData?.data || [];
  const totalWarranties = activeData?.total || 0;
  const totalPages = Math.ceil(totalWarranties / PAGE_SIZES.SERVICE_REQUESTS);
  
  // Loading durumunu belirle
  const isLoading = isSearchMode ? searchLoading : loading;
  const isRefreshing = isSearchMode ? searchRefreshing : refreshing;

  // Arama fonksiyonu
  const handleSearch = async (query: string, searchType: 'serial' | 'invoice') => {
    try {
      setIsActiveSearch(true);
      
      // Arama öncesi mevcut filtreleri kaydet
      setPreSearchStatuses(selectedStatuses);
      setPreSearchStartDate(startDate);
      setPreSearchEndDate(endDate);
      
      // Filtreleri sıfırla
      setSelectedStatuses([]);
      setStartDate('');
      setEndDate('');
      setCurrentPage(1);
      
      // URL güncelle
      updateUrlParams({
        searchMode: true,
        searchQuery: query,
        searchType: searchType,
        page: 1,
        statuses: [],
        startDate: '',
        endDate: ''
      });
      
      // State'leri güncelle
      setIsSearchMode(true);
      setSearchQuery(query);
      setSearchType(searchType);
      
    } catch (error) {
      console.error('Arama hatası:', error);
      showToast('Arama sırasında bir hata oluştu.', 'error');
    }
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSelectedStatuses([]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    
    // URL güncelle
    updateUrlParams({
      statuses: [],
      startDate: '',
      endDate: '',
      page: 1
    });
  };

  // Tarih değişiklik fonksiyonları
  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    setCurrentPage(1);
    updateUrlParams({
      startDate: date,
      page: 1
    });
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    setCurrentPage(1);
    updateUrlParams({
      endDate: date,
      page: 1
    });
  };

  // Component mount edildikten sonra flag'i set et
  useEffect(() => {
    setIsComponentMounted(true);
  }, []);

  // Navigation türüne göre scroll yönetimi
  useEffect(() => {
    if (!isComponentMounted) return;

    const currentUrl = window.location.pathname + window.location.search;
    const scrollKey = `scroll-${currentUrl}`;

    // POP navigation (geri/ileri tuş) - scroll pozisyonunu restore et
    if (navigationType === 'POP') {
      const savedPosition = sessionStorage.getItem(scrollKey);
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition));
      }
    }
    // PUSH/REPLACE navigation - scroll'u en üste al
    else if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      window.scrollTo(0, 0);
    }
  }, [navigationType, isComponentMounted]);

  // Sayfa ayrılırken scroll pozisyonunu kaydet
  useEffect(() => {
    const saveScrollPosition = () => {
      const currentUrl = window.location.pathname + window.location.search;
      const scrollKey = `scroll-${currentUrl}`;
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      sessionStorage.setItem(scrollKey, scrollPosition.toString());
    };

    // Scroll pozisyonunu periyodik olarak kaydet
    const handleScroll = () => {
      if (scrollSaveTimeout.current) {
        clearTimeout(scrollSaveTimeout.current);
      }
      scrollSaveTimeout.current = setTimeout(saveScrollPosition, 100);
    };

    // Sayfa ayrılırken kaydet
    const handleBeforeUnload = saveScrollPosition;

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveScrollPosition();
    };
  }, []);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    const currentLoading = isSearchMode ? searchLoading : loading;
    
    if (!currentLoading && isComponentMounted) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [loading, searchLoading, isComponentMounted, isSearchMode]);

  // Hata durumunda toast göster
  useEffect(() => {
    if (warrantiesError) {
      console.error('Garantiler yüklenirken hata:', warrantiesError);
      showToast('Garantiler yüklenirken bir hata oluştu.', 'error');
    }
  }, [warrantiesError]);

  useEffect(() => {
    if (searchError) {
      console.error('Arama sonuçları yüklenirken hata:', searchError);
      showToast('Arama sonuçları yüklenirken bir hata oluştu.', 'error');
    }
  }, [searchError]);

  // Arama sonuçları için toast göster
  useEffect(() => {
    if (isSearchMode && searchResultsData && isActiveSearch && currentPage === 1) {
      if (searchResultsData.total === 0) {
        showToast('Arama kriterlerinize uygun garanti bulunamadı.', 'warning');
      } else {
        showToast(`${searchResultsData.total} adet sonuç bulundu`, 'success');
      }
      setIsActiveSearch(false);
    }
  }, [searchResultsData, isActiveSearch, currentPage, isSearchMode]);

  // Sayfa değiştir
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams({ page });
  };

  // Scroll olayını dinle - butonları gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      
      const threshold = 10;
      if (st > lastScrollTop.current + threshold) {
        setShowNewRequestButton(false);
        setShowSearchButton(false);
      } else if (st < lastScrollTop.current - threshold) {
        setShowNewRequestButton(true);
        setShowSearchButton(true);
      }
      
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 relative">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                 <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                   <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
                   </svg>
                 </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Garantilerim
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Garanti bilgilerinizi görüntüleyin
                  </p>
                </div>
              </div>
            </div>
          </div>
          
           {/* Yeni Garanti Butonu */}
           <AnimatePresence>
             {!isLoading && !isInitialLoad && showNewRequestButton && !(isMobile && isSidebarOpen) && (
               <motion.div
                 className="fixed top-4 right-4 z-50"
                 initial={{ scale: 0, opacity: 0, y: -20 }}
                 animate={{ scale: 1, opacity: 1, y: 0 }}
                 exit={{ scale: 0, opacity: 0, y: -20 }}
                 transition={{
                   type: "spring",
                   stiffness: 300,
                   damping: 20,
                   duration: 0.4
                 }}
               >
                 <motion.button
                   onClick={() => navigate('/warranty-start')}
                   className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                   aria-label="Yeni Garanti"
                   whileHover={{ scale: 1.1 }}
                   whileTap={{ scale: 0.95 }}
                   transition={{ type: "spring", stiffness: 400, damping: 17 }}
                 >
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                     <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                   </svg>
                 </motion.button>
               </motion.div>
             )}
           </AnimatePresence>

           {/* Arama Butonu - Yeni garanti butonunun altında */}
           <AnimatePresence>
             {!isLoading && !isInitialLoad && showSearchButton && !(isMobile && isSidebarOpen) && (
               <motion.div
                 className="fixed top-16 right-4 z-50"
                 initial={{ scale: 0, opacity: 0, y: -20 }}
                 animate={{ scale: 1, opacity: 1, y: 0 }}
                 exit={{ scale: 0, opacity: 0, y: -20 }}
                 transition={{
                   type: "spring",
                   stiffness: 300,
                   damping: 20,
                   delay: 0.1
                 }}
               >
                 <motion.button
                   onClick={() => setIsSearchModalOpen(true)}
                   className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                   aria-label="Arama"
                   whileHover={{ scale: 1.1 }}
                   whileTap={{ scale: 0.95 }}
                   transition={{ type: "spring", stiffness: 400, damping: 17 }}
                 >
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                     <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                   </svg>
                 </motion.button>
               </motion.div>
             )}
           </AnimatePresence>
        </div>

        {/* Arama Durumu */}
        {isSearchMode && !isLoading && (
          <div className="border border-blue-200/50 dark:border-blue-700/50 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 shadow-sm mb-6">
            <div className="rounded-xl p-5 border border-white dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                    Arama: "{searchQuery}"
                  </p>
                   <p className="text-xs text-blue-600 dark:text-blue-400">
                     {searchType === 'serial' ? 'Seri numarasına göre' : 'Fatura numarasına göre'} • {totalWarranties} sonuç
                   </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsSearchMode(false);
                  setSearchQuery('');
                  setCurrentPage(1);
                  
                  // Arama öncesi filtreleri geri yükle
                  setSelectedStatuses(preSearchStatuses);
                  setStartDate(preSearchStartDate);
                  setEndDate(preSearchEndDate);
                  
                  // URL güncelle
                  updateUrlParams({
                    searchMode: false,
                    page: 1,
                    statuses: preSearchStatuses,
                    startDate: preSearchStartDate,
                    endDate: preSearchEndDate
                  });
                }}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 p-1 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            </div>
          </div>
        )}

        {/* Filtre Bölümü - Arama modunda gizle */}
        {!isSearchMode && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mb-6">
          <div className="rounded-xl p-6 border border-white dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* Filtre Başlığı ve Toggle */}
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filtreler</h3>
              <button
                onClick={() => {
                  const newShowFilters = !showFilters;
                  setShowFilters(newShowFilters);
                  updateUrlParams({ showFilters: newShowFilters });
                }}
                className="sm:hidden inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <svg className={`w-4 h-4 mr-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
                {showFilters ? 'Gizle' : 'Göster'}
              </button>
            </div>

            {/* Aktif Filtre Sayısı */}
            {(selectedStatuses.length > 0 || startDate || endDate) && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedStatuses.length + (startDate ? 1 : 0) + (endDate ? 1 : 0)} filtre aktif
                </span>
                <button
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  Temizle
                </button>
              </div>
            )}
          </div>

          {/* Filtre İçeriği */}
          <div className={`${showFilters ? 'block' : 'hidden'} sm:block mt-4 pt-4 border-t border-gray-200 dark:border-gray-600`}>
            <div className="space-y-6">
              {/* Durum Filtreleri */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Garanti Durumu
                </label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((status) => (
                    <label
                      key={status}
                      className="inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleStatusChange(status)}
                        className="sr-only"
                      />
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        selectedStatuses.includes(status)
                          ? getStatusColor(status)
                          : 'bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 text-gray-600 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 dark:text-gray-400 hover:from-gray-200 hover:via-slate-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          selectedStatuses.includes(status) ? 'bg-current' : 'bg-gray-400'
                        }`}></div>
                        <span className="sm:hidden">
                          {status === IslemAdi.ONAY_BEKLIYOR ? 'Bekliyor' : 
                           status === IslemAdi.ONAYLANDI ? 'Onaylı' : 
                           status === IslemAdi.IPTAL_EDILDI ? 'İptal' : status}
                        </span>
                        <span className="hidden sm:inline">
                          {status}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tarih Filtreleri */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                  İşlem Tarihi
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DatePicker
                    label="Başlangıç Tarihi"
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="Başlangıç tarihi seçin..."
                  />
                  <DatePicker
                    label="Bitiş Tarihi"
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="Bitiş tarihi seçin..."
                  />
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        )}

        {/* Loading State */}
        {(isLoading || !isComponentMounted || isRefreshing) ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg">
            <div className="rounded-xl p-6 border border-white dark:border-gray-700">
             <div className="flex items-center justify-center py-12">
               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
             </div>
            </div>
          </div>
        ) : warranties.length > 0 ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden">
            <div className="rounded-xl border border-white dark:border-gray-700 overflow-hidden">
            {/* Başlık */}
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                  {selectedStatuses.length > 0 ? (
                    `${totalWarranties} garanti bulundu`
                  ) : (
                    `Toplam ${totalWarranties} garanti`
                  )}
                   {isRefreshing && (
                     <div className="ml-3 animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500"></div>
                   )}
                </h2>
              </div>
            </div>

            {/* Garanti Kartları */}
            <div className={`p-6 space-y-4 transition-opacity duration-300 ${isRefreshing ? 'opacity-60' : 'opacity-100'}`}>
              {warranties.map((warranty: any, index: number) => (
                <motion.div
                  key={warranty.Id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative border border-gray-200 dark:border-gray-600 rounded-xl bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700/50 dark:via-gray-700/40 dark:to-gray-600/50 hover:shadow-md hover:from-white hover:via-gray-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:via-gray-700/90 dark:hover:to-gray-600 cursor-pointer transition-all duration-200 overflow-hidden"
                  onClick={(event) => handleWarrantyClick(event, warranty.Id)}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="rounded-xl p-6 border border-white dark:border-gray-700">
                  {/* Ripple Effects */}
                   {ripples[warranty.Id]?.map((ripple) => (
                     <motion.div
                       key={ripple.id}
                       className="absolute rounded-full bg-indigo-400/30 pointer-events-none"
                      style={{
                        left: ripple.x - (ripple.size || 50) / 2,
                        top: ripple.y - (ripple.size || 50) / 2,
                        width: ripple.size || 50,
                        height: ripple.size || 50,
                      }}
                      initial={{ scale: 0.1, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  ))}

                  {/* Ok İkonu - Üst Sağ Köşe */}
                  <div className="absolute top-4 right-4 text-gray-400 dark:text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  <div className="flex flex-col space-y-4">
                    {/* Üst Kısım - Başlık ve Durum */}
                    <div className="flex flex-col space-y-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 pr-8">
                      <div className="flex items-center space-x-3">
                         <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 border border-indigo-100 dark:border-gray-600">
                           <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
                           </svg>
                         </div>
                        <div className="min-w-0 flex-1">
                           <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                             <span className="hidden sm:inline">Seri No: </span>{warranty.Sasi}
                           </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate">{formatDate(warranty.IslemTarihi)}</span>
                          </p>
                        </div>
                       </div>
                       
                       <div className="flex items-center justify-between sm:flex-col sm:items-end sm:space-y-2">
                         <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getStatusColor(warranty.IslemAdi)}`}>
                           <div className="w-2 h-2 rounded-full bg-current mr-2 flex-shrink-0"></div>
                           {warranty.IslemAdi}
                         </span>
                       </div>
                    </div>

                    {/* Orta Kısım - Ana Bilgiler */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 h-full">
                        <div className="rounded-lg p-4 border border-white dark:border-gray-700 h-full">
                        <div className="flex items-start justify-between h-full">
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide">KATEGORİ</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{warranty.KategoriAdi}</p>
                          </div>
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-100 via-sky-100 to-blue-200 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 border border-blue-200 dark:border-blue-700">
                            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                        </div>
                        </div>
                      </div>                      
                     
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 h-full">
                        <div className="rounded-lg p-4 border border-white dark:border-gray-700 h-full">
                        <div className="flex items-start justify-between h-full">
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide">MARKA - MODEL</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{warranty.MarkaAdi}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{warranty.ModelAdi}</p>
                          </div>
                          <div className="w-8 h-8 bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 border border-green-200 dark:border-green-700">
                            <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                          </div>
                        </div>
                        </div>
                      </div>

                       <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 h-full">
                         <div className="rounded-lg p-4 border border-white dark:border-gray-700 h-full">
                         <div className="flex items-start justify-between h-full">
                           <div className="flex-1">
                             <p className="text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide">GARANTİ BİTİŞ</p>
                             <p className="text-lg font-bold text-gray-900 dark:text-white">{formatWarrantyEndDate(warranty)}</p>
                             <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${getWarrantyStatusColor(warranty)}`}>
                               <div className="w-1.5 h-1.5 rounded-full bg-current mr-1.5 flex-shrink-0"></div>
                               {warranty.SonIslem === ActionType.CANCELLED ? 'Garanti Dışı' : warranty.GarantiDurumuAciklamasi}
                             </span>
                           </div>
                           <div className="w-8 h-8 bg-gradient-to-br from-purple-100 via-violet-100 to-purple-200 dark:from-purple-900/30 dark:via-purple-900/25 dark:to-purple-800/30 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 border border-purple-200 dark:border-purple-700">
                             <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                           </div>
                         </div>
                         </div>
                       </div>
                    </div>

                    {/* Alt Kısım - Satıcı Bilgileri */}
                    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-blue-800/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="rounded-lg p-3 border border-white dark:border-gray-700">
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 border border-blue-200 dark:border-blue-700 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-blue-700 dark:text-blue-300 tracking-wide">SATIŞ BİLGİLERİ</p>
                            <div className="block sm:hidden">
                              {/* Mobil: İki satır */}
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                {warranty.CariAdi}
                              </p>
                              <p className="text-xs text-blue-600 dark:text-blue-400">
                                {[warranty.SaticiIlceAdi, warranty.SaticiIlAdi].filter(Boolean).join(' / ')}
                              </p>
                            </div>
                            <div className="hidden sm:block">
                              {/* Desktop: Tek satır */}
                              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                {warranty.CariAdi} - {[warranty.SaticiIlceAdi, warranty.SaticiIlAdi].filter(Boolean).join(' / ')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-sm mt-6">
                <div className="rounded-xl p-4 sm:p-5 border border-white dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center justify-center sm:justify-start">
                     <div className="w-8 h-8 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-lg flex items-center justify-center mr-3 border border-indigo-100 dark:border-gray-600">
                       <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                       </svg>
                     </div>
                    <div className="text-center sm:text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Sayfa {currentPage} / {totalPages}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Toplam {totalWarranties} garanti
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center sm:justify-end space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="group border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <div className="rounded-xl border border-white dark:border-gray-700 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:text-gray-400 dark:disabled:text-gray-500">
                        <svg className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Önceki
                      </div>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="group border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <div className="rounded-xl border border-white dark:border-gray-700 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:text-gray-400 dark:disabled:text-gray-500">
                        Sonraki
                        <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
                </div>
              </div>
            )}
            </div>
          </div>
        ) : isComponentMounted ? (
          /* Boş Durum */
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg">
            <div className="rounded-xl p-6 border border-white dark:border-gray-700">
            <div className="text-center py-12">
               <div className="flex justify-center mb-6">
                 <div className="w-20 h-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-2xl flex items-center justify-center shadow-lg border border-indigo-100 dark:border-gray-600">
                   <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
                   </svg>
                 </div>
               </div>
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {(selectedStatuses.length > 0 || startDate || endDate) ? (
                  'Seçilen kriterlere uygun garanti bulunamadı'
                ) : (
                  'Henüz garanti kaydınız bulunmuyor'
                )}
              </h2>
              
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                {(selectedStatuses.length > 0 || startDate || endDate) ? (
                  <>
                    {selectedStatuses.length > 0 && `${selectedStatuses.join(', ')} durumunda`}
                    {(startDate || endDate) && (selectedStatuses.length > 0 ? ' ve ' : '') + 'seçilen tarih aralığında'} 
                    &nbsp;garanti bulunmuyor. Filtreleri temizleyerek tüm garantilerinizi görüntüleyebilirsiniz.
                  </>
                ) : (
                  'Ürününüz için garanti kaydı oluşturarak garanti kapsamındaki hizmetlerden faydalanabilirsiniz.'
                )}
              </p>

              <div className="flex justify-center">
                 <button
                   onClick={(selectedStatuses.length > 0 || startDate || endDate) ? clearFilters : () => navigate('/warranty-start')}
                   className="group w-full md:w-auto font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-[1.02] active:scale-[0.98] text-white border border-blue-500 hover:border-blue-600 flex items-center justify-center gap-2 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                 >
                {(selectedStatuses.length > 0 || startDate || endDate) ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Filtreleri Temizle
                  </>
                ) : (
                  <>
                    Garanti Başlatma
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
                    </svg>
                  </>
                )}
                </button>
              </div>
            </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Toast */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      {/* Arama modal'ı */}
      <SerialSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSearch={handleSearch}
        searchLoading={searchLoading}
        title="Garanti Ara"
      />
    </PageTransition>
  );
}
